import * as Y from "yjs";
import type { NewBlock } from "./blocks";
import type { BlockType, Point } from "./types";

export const BLOCKS_KEY = "blocks";

/** Props that are edited character by character and therefore live in a Y.Text. */
const TEXT_PROPS: Partial<Record<BlockType, string[]>> = {
  note: ["text"],
  text: ["text"],
  card: ["text"],
};

export type StoredBlock = {
  id: string;
  type: BlockType;
  x: number;
  y: number;
  w: number;
  h: number;
  z: number;
  createdBy: string;
  createdAt: number;
  accent?: string;
  locked?: boolean;
  props: Record<string, any>;
};

export function blocksMap(doc: Y.Doc): Y.Map<Y.Map<any>> {
  return doc.getMap<Y.Map<any>>(BLOCKS_KEY);
}

function toShared(value: unknown): unknown {
  if (Array.isArray(value)) {
    const array = new Y.Array<unknown>();
    array.push(value.map((item) => toShared(item)));
    return array;
  }
  if (value && typeof value === "object") {
    const map = new Y.Map<unknown>();
    for (const [key, item] of Object.entries(value as Record<string, unknown>)) {
      map.set(key, toShared(item));
    }
    return map;
  }
  return value;
}

function propsToShared(type: BlockType, props: Record<string, any>): Y.Map<any> {
  const map = new Y.Map<any>();
  const textKeys = TEXT_PROPS[type] ?? [];
  for (const [key, value] of Object.entries(props)) {
    if (textKeys.includes(key)) {
      const text = new Y.Text();
      if (typeof value === "string" && value.length > 0) text.insert(0, value);
      map.set(key, text);
    } else {
      map.set(key, toShared(value));
    }
  }
  return map;
}

export function addBlock(doc: Y.Doc, block: NewBlock): NewBlock {
  const { props, ...rest } = block;
  doc.transact(() => {
    const map = new Y.Map<any>();
    for (const [key, value] of Object.entries(rest)) map.set(key, value);
    map.set("props", propsToShared(block.type, props));
    blocksMap(doc).set(block.id, map);
  });
  return block;
}

export function readBlock(doc: Y.Doc, id: string): StoredBlock | null {
  const map = blocksMap(doc).get(id);
  return map ? (map.toJSON() as StoredBlock) : null;
}

export function readBlocks(doc: Y.Doc): StoredBlock[] {
  return [...blocksMap(doc).values()].map((map) => map.toJSON() as StoredBlock);
}

export function blockIds(doc: Y.Doc): string[] {
  return [...blocksMap(doc).keys()];
}

export function updateBlock(doc: Y.Doc, id: string, patch: Partial<StoredBlock>): void {
  const map = blocksMap(doc).get(id);
  if (!map) return;
  doc.transact(() => {
    for (const [key, value] of Object.entries(patch)) {
      if (key === "props") continue;
      map.set(key, value);
    }
  });
}

export function updateBlockProps(doc: Y.Doc, id: string, patch: Record<string, unknown>): void {
  const props = blocksMap(doc).get(id)?.get("props") as Y.Map<any> | undefined;
  if (!props) return;
  doc.transact(() => {
    for (const [key, value] of Object.entries(patch)) {
      const existing = props.get(key);
      if (existing instanceof Y.Text && typeof value === "string") {
        existing.delete(0, existing.length);
        existing.insert(0, value);
      } else {
        props.set(key, toShared(value));
      }
    }
  });
}

export function moveBlocks(doc: Y.Doc, ids: string[], delta: Point): void {
  doc.transact(() => {
    for (const id of ids) {
      const map = blocksMap(doc).get(id);
      if (!map || map.get("locked")) continue;
      map.set("x", Math.round(map.get("x") + delta.x));
      map.set("y", Math.round(map.get("y") + delta.y));
    }
  });
}

export function removeBlocks(doc: Y.Doc, ids: string[]): void {
  doc.transact(() => {
    for (const id of ids) blocksMap(doc).delete(id);
  });
}

export function topZ(doc: Y.Doc): number {
  return [...blocksMap(doc).values()].reduce((max, map) => Math.max(max, map.get("z") ?? 0), 0);
}

export function bringToFront(doc: Y.Doc, ids: string[]): void {
  doc.transact(() => {
    let z = topZ(doc);
    for (const id of ids) {
      z += 1;
      blocksMap(doc).get(id)?.set("z", z);
    }
  });
}

export function sendToBack(doc: Y.Doc, ids: string[]): void {
  doc.transact(() => {
    const min = [...blocksMap(doc).values()].reduce((acc, map) => Math.min(acc, map.get("z") ?? 0), 0);
    let z = min;
    for (const id of ids) {
      z -= 1;
      blocksMap(doc).get(id)?.set("z", z);
    }
  });
}

export function blockProps(doc: Y.Doc, id: string): Y.Map<any> | undefined {
  return blocksMap(doc).get(id)?.get("props") as Y.Map<any> | undefined;
}
